"use client";

import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction
} from "react";

import { removeAccents } from "@/utils/helpers";

import type { BreedListItem } from "../breeds.types";

interface UseBreedFilterProps {
  breeds: BreedListItem[];
  isMenuOpen: boolean;
  updateFilters: Dispatch<SetStateAction<BreedListItem[]>>;
  toggleMenu: () => void;
}

export const useBreedFilter = ({
  breeds,
  updateFilters,
  isMenuOpen,
  toggleMenu
}: UseBreedFilterProps) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [showLoader, setShowLoader] = useState<boolean>(false);

  const removeFilterSelection = useCallback(
    (filterToRemove: string) => {
      const updatedFilters = selectedFilters.filter(
        (filter) => filter !== filterToRemove
      );

      setSelectedFilters(updatedFilters);
      if (!updatedFilters?.length) updateFilters(breeds);

      if (isMenuOpen) {
        toggleMenu();
      }
    },
    [selectedFilters, breeds, updateFilters, isMenuOpen, toggleMenu]
  );

  const removeAllFilters = useCallback(() => {
    setSelectedFilters([]);
    updateFilters(breeds);

    if (isMenuOpen) {
      toggleMenu();
    }
  }, [updateFilters, setSelectedFilters, breeds, isMenuOpen, toggleMenu]);

  const addFilterSelection = useCallback(
    (filterValue: string) => {
      const normalizedValue = filterValue === "Médio" ? "Medio" : filterValue;

      if (selectedFilters.includes(normalizedValue)) {
        removeFilterSelection(normalizedValue);
        return;
      }

      if (isMenuOpen) {
        toggleMenu();
      }

      setSelectedFilters((prevFilters) => [...prevFilters, normalizedValue]);
      setShowLoader(true);
    },
    [selectedFilters, isMenuOpen, toggleMenu, removeFilterSelection]
  );

  const includesNormalized = (
    value: string,
    filterList?: string[]
  ): boolean => {
    if (!filterList) return false;

    const normalizedValue = removeAccents(value.toLowerCase());
    const normalizedFilters = filterList.map((filter) =>
      removeAccents(filter.toLowerCase())
    );

    return normalizedFilters.includes(normalizedValue);
  };

  const getEnergyLevel = (progress: number): string => {
    if (progress >= 1 && progress <= 3) return "Energia baixa";
    if (progress >= 4 && progress <= 6) return "Energia média";
    if (progress >= 7 && progress <= 10) return "Energia alta";
    return "";
  };

  const getFilterList = useCallback(() => {
    const filteredBreeds = breeds.filter((breed) => {
      const {
        tamanho,
        pelagem,
        classificacaoFIFE,
        gruposFCI,
        caracteristicasComportamentais
      } = breed;

      const tamanhoFilter = removeAccents(tamanho || "").toLowerCase();

      const energyFilter = caracteristicasComportamentais?.filter(
        (caracteristica) => caracteristica.caracteristica === "Energia"
      );

      const energyLevel = energyFilter?.length
        ? getEnergyLevel(energyFilter[0].progresso)
        : "";

      return (
        includesNormalized(tamanhoFilter, selectedFilters) ||
        includesNormalized(pelagem, selectedFilters) ||
        includesNormalized(classificacaoFIFE || "", selectedFilters) ||
        includesNormalized(gruposFCI || "", selectedFilters) ||
        includesNormalized(energyLevel, selectedFilters)
      );
    });

    updateFilters(filteredBreeds);
  }, [breeds, selectedFilters, updateFilters]);

  useEffect(() => {
    if (selectedFilters.length > 0) {
      getFilterList();
    }
  }, [selectedFilters, breeds, updateFilters, getFilterList]);

  return {
    selectedFilters,
    setSelectedFilters,
    addFilterSelection,
    removeFilterSelection,
    removeAllFilters,
    setShowLoader,
    showLoader
  };
};
